/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getData } from "../../Services/getData";
import { paymentTypeEnum, StatusEnum, UrlEnum } from "../../Utils/Utils";
import {
  Typography,
  CircularProgress,
  Button,
  Divider,
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from "@mui/material";
import { toast } from "react-toastify";
import { Vocabulary } from "../../Utils/Vocabulary";
import moment from "moment";
import { updateData } from "../../Services/updateData";
import style from "./Orders.module.scss";

export default function CustomerOrder() {
  const { id } = useParams();
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [trackingInfo, setTrackingInfo] = useState<any>(null);

  useEffect(() => {
    fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   *
   */
  const fetchOrder = async () => {
    setLoading(true);
    await getData(`${UrlEnum.orders}/${id}`)
      .then((res) => {
        if (res && res.data) {
          setOrder(res.data[0]);
          if (res.data[0].fanCourierInfo?.awb) {
            getData(`${UrlEnum.trackAwb}/${res.data[0].fanCourierInfo?.awb}`)
              .then((res) => {
                if (res && res.data && res.data.length) {
                  setTrackingInfo(res.data[0]);
                }
              });
          }
        }
      }).catch((err) => {
        toast.error("Eroare la încărcarea comenzii.");
      });

    setLoading(false);
  };

  const handleCancelOrder = async () => {
    setLoading(true);
    await updateData(`${UrlEnum.orders}/cancelOrder/${id}`)
      .then((res) => {
        if (!res) {
          toast.error("Eroare la anularea comenzii.");
        }
        if (res && res.data) {
          setOrder(res.data);
        }
        setLoading(false);
      })
      .catch((err) => {
        toast.error("Eroare la anularea comenzii.");
        setLoading(false);
      });
  };

  if (loading) return <CircularProgress />;

  if (!order) return <Typography>{Vocabulary.orderNotFound}</Typography>;

  return (
    <Box sx={{ maxWidth: "100%", mx: "auto", mt: 4, padding: "20px" }}>
      <Typography style={{ fontSize: "22px", fontWeight: 500 }} gutterBottom>
        {Vocabulary.order} {order.orderNumber}{" "}
      </Typography>
      <Typography style={{ fontSize: "15px", fontWeight: 200 }}>
        {order.deviceDetails?.deviceName}, &nbsp;{order.deviceDetails?.storage},
        &nbsp;{order.deviceDetails?.color}
      </Typography>

      {order.status?.order === StatusEnum.cancelled && (
        <Typography variant="h5" color="error" gutterBottom>
          Comanda este anulată
        </Typography>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Status */}
      {order.status?.order !== StatusEnum.cancelled && (
        <div className={style.customerOrderStatus}>
          <Typography variant="h6" gutterBottom>
            {Vocabulary.status}: {order.status?.order || ""}
          </Typography>
          <Divider sx={{ my: 2 }} />
        </div>
      )}

      {/* Order Info */}
      <Typography variant="h6" gutterBottom>
        Informații Comanda
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography>
          <strong>Data comenzii:</strong>
        </Typography>
        <Typography>
          {new Date(order.createdAt).toLocaleString("ro-RO")}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography>
          <strong>Preț estimat:</strong>
        </Typography>
        <Typography><strong>{order.estimatedPrice}</strong> RON</Typography>
      </Box>
      <Divider sx={{ my: 2 }} />

      {/* Payment Info */}
      <Typography variant="h6" gutterBottom>
        Informații de plată
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography>
          <strong>Tipul plății:</strong>
        </Typography>
        <Typography>{order.paymentType || ""}</Typography>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography>
          <strong>Termen de plată:</strong>
        </Typography>
        <Typography>
          {order && order.paymentType
            ? order.paymentType === paymentTypeEnum.consignment
              ? "Plata in 30 zile"
              : order.paymentType
            : ""}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography>
          <strong>IBAN:</strong>
        </Typography>
        <Typography>
          {order.customer?.personalData?.iban || "Nu este disponibil"}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography>
          <strong>Nume :</strong>
        </Typography>
        <Typography>
          {order.customer?.personalData?.accountFullName ||
            "Nu este disponibil"}
        </Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Shipping & Delivery Info */}
      <Typography variant="h6" gutterBottom>
        Informații de predare
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography>
          <strong>Adresa de livrare:</strong>
        </Typography>
        <Typography>
          {order.shipping?.street}, {order.shipping?.number},{" "}
          {order.shipping?.city}, {order.shipping?.county}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography>
          <strong>Data ridicării:</strong>
        </Typography>
        <Typography>
          {moment(order.shipping?.pickupDate).format("DD-MM-YYYY")}
        </Typography>
      </Box>

      <Divider sx={{ my: 2 }} />
      {/* Fan Courier Info */}
      <Typography variant="h6" gutterBottom>
        Informații Curier
      </Typography>
      {order.fanCourierInfo && (
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Typography>
            <strong>AWB:</strong>
          </Typography>
          <Typography>{order?.fanCourierInfo?.awb}</Typography>
        </Box>
      )}
      {(trackingInfo && trackingInfo?.events) && (
        <Stepper orientation="vertical">
          {trackingInfo?.events.map((step: { label: string; location: string; date: string; name: string; }, index: number) => (
            <Step key={step.label} active>
              <StepLabel>
                <Typography><strong>{step.name}</strong></Typography>
              </StepLabel>
              <StepContent>
                <Typography>{step.location}</Typography>
                <Typography>{step.date}</Typography>
              </StepContent>
            </Step>
          ))}</Stepper>
      )}


      <Divider sx={{ my: 2 }} />
      {/* Cancel Order Button, only show if not canceled */}
      {order.status?.order !== StatusEnum.cancelled && (
        <Box mt={3}>
          <Button variant="outlined" color="error" onClick={handleCancelOrder}>
            Anulează comanda
          </Button>
        </Box>
      )}
    </Box>
  );
}
